import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useActiveBloodRequests, expressInterest, fulfillBloodRequest, acceptBloodRequest } from '../hooks/useSupabase';
import { DISTRICTS_KERALA, BLOOD_GROUPS, getCompatibleBloodGroups } from '../types/schema';
import ConfirmDialog from './ConfirmDialog';
import { useConfirm } from '../hooks/useConfirm';

/* ─── Helpers ──────────────────────────────────────────────── */
const getUrgencyConfig = (urgency) => {
  const map = {
    critical: { badgeCls: 'badge-critical', label: 'Critical', dot: 'bg-crimson-500', border: 'border-crimson-400', glow: 'shadow-glow-sm' },
    high:     { badgeCls: 'badge-high',     label: 'High',     dot: 'bg-orange-500',  border: 'border-orange-300', glow: '' },
    medium:   { badgeCls: 'badge-medium',   label: 'Medium',   dot: 'bg-amber-500',   border: 'border-amber-300',  glow: '' },
    low:      { badgeCls: 'badge-low',      label: 'Low',      dot: 'bg-sky-500',     border: 'border-sky-300',    glow: '' },
  };
  return map[urgency] || map.low;
};

const getStatusConfig = (status) => {
  const map = {
    active:    { badgeCls: 'badge-success', label: 'Active',    icon: '🟢' },
    fulfilled: { badgeCls: 'badge-info',    label: 'Fulfilled', icon: '✅' },
    expired:   { badgeCls: 'badge-neutral', label: 'Expired',   icon: '⏰' },
    cancelled: { badgeCls: 'badge-crimson', label: 'Cancelled', icon: '❌' },
  };
  return map[status] || { badgeCls: 'badge-neutral', label: 'Unknown', icon: '❓' };
};

const getTimeAgo = (date) => {
  const diffH = Math.floor((Date.now() - new Date(date)) / 3600000);
  if (diffH < 1) return 'Just now';
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD}d ago`;
};

/* ─── Skeleton ─────────────────────────────────────────────── */
const CardSkeleton = () => (
  <div className="card p-6 animate-pulse">
    <div className="flex justify-between mb-4">
      <div className="flex-1">
        <div className="skeleton h-5 w-40 mb-2 rounded-lg" />
        <div className="skeleton h-3 w-24 rounded-lg" />
      </div>
      <div className="skeleton w-14 h-14 rounded-2xl" />
    </div>
    <div className="skeleton h-3 w-full mb-2 rounded" />
    <div className="skeleton h-3 w-3/4 rounded" />
    <div className="mt-4 flex gap-2">
      <div className="skeleton h-9 flex-1 rounded-xl" />
      <div className="skeleton h-9 flex-1 rounded-xl" />
    </div>
  </div>
);

/* ─── Main ─────────────────────────────────────────────────── */
const BloodRequestsDashboard = ({ currentDonor }) => {
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedBloodGroup, setSelectedBloodGroup] = useState('');
  const { data: requests, loading } = useActiveBloodRequests();
  const [actionLoading, setActionLoading] = useState({});
  const [showDonorModal, setShowDonorModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [donorFormData, setDonorFormData] = useState({ name: '', phoneNumber: '', location: '', district: '', bloodGroup: '' });
  const { isOpen, config, confirm, handleClose } = useConfirm();

  const handleExpressInterest = async (requestId) => {
    if (!currentDonor) { toast.error('Please register as a donor first.'); return; }
    setActionLoading(p => ({ ...p, [requestId]: 'interest' }));
    try {
      await expressInterest(requestId, currentDonor.id);
      toast.success('Interest expressed! 👍');
    } catch { toast.error('Failed. Please try again.'); }
    finally { setActionLoading(p => ({ ...p, [requestId]: null })); }
  };

  const handleFulfillRequest = async (requestId) => {
    if (!currentDonor) { toast.error('Please register as a donor first.'); return; }
    confirm({
      title: 'Mark as Fulfilled?',
      message: 'Confirm this blood request has been fulfilled. This cannot be undone.',
      confirmText: 'Yes, Mark Fulfilled',
      cancelText: 'Cancel',
      type: 'success',
      onConfirm: async () => {
        setActionLoading(p => ({ ...p, [requestId]: 'fulfill' }));
        try {
          await fulfillBloodRequest(requestId, currentDonor.id);
          toast.success('Request marked as fulfilled! 🎉');
        } catch { toast.error('Failed. Please try again.'); }
        finally { setActionLoading(p => ({ ...p, [requestId]: null })); }
      }
    });
  };

  const handleReadyToDonate = (requestId) => {
    if (currentDonor) {
      handleAcceptRequest(requestId, {
        donorId: currentDonor.id, name: currentDonor.name,
        phoneNumber: currentDonor.phoneNumber, location: currentDonor.location,
        district: currentDonor.district, bloodGroup: currentDonor.bloodGroup, isRegistered: true
      });
    } else {
      setSelectedRequestId(requestId);
      setShowDonorModal(true);
    }
  };

  const handleAcceptRequest = async (requestId, donorInfo) => {
    setActionLoading(p => ({ ...p, [requestId]: 'accept' }));
    try {
      await acceptBloodRequest(requestId, donorInfo);
      toast.success('Thank you! The requester will be notified. 🙏');
      setShowDonorModal(false);
      setDonorFormData({ name: '', phoneNumber: '', location: '', district: '', bloodGroup: '' });
    } catch { toast.error('Failed. Please try again.'); }
    finally { setActionLoading(p => ({ ...p, [requestId]: null })); }
  };

  const handleDonorFormSubmit = (e) => {
    e.preventDefault();
    if (!donorFormData.name?.trim() || donorFormData.name.trim().length < 2) { toast.error('Enter your name'); return; }
    if (!/^\d{10}$/.test(donorFormData.phoneNumber.replace(/\D/g, ''))) { toast.error('Enter a valid 10-digit phone number'); return; }
    if (!donorFormData.location || !donorFormData.district) { toast.error('Provide your location and district'); return; }
    handleAcceptRequest(selectedRequestId, { ...donorFormData, isRegistered: false });
    setShowDonorModal(false);
  };

  const isCompatibleDonor = (reqBG, donorBG) => {
    if (!donorBG) return false;
    return getCompatibleBloodGroups(reqBG).includes(donorBG);
  };

  const filteredRequests = (requests?.filter(r => {
    if (selectedBloodGroup && r.bloodGroup !== selectedBloodGroup) return false;
    if (selectedDistrict && r.hospital?.district !== selectedDistrict) return false;
    return true;
  }) || []).sort((a, b) => {
    // Active requests first, fulfilled at the bottom
    if (a.status === 'fulfilled' && b.status !== 'fulfilled') return 1;
    if (a.status !== 'fulfilled' && b.status === 'fulfilled') return -1;
    return 0;
  });
  const activeCount = filteredRequests.filter(r => r.status !== 'fulfilled').length;

  return (
    <div className="page-shell">
      {/* ── Page Header ── */}
      <div className="relative bg-hero overflow-hidden">
        <div className="absolute inset-0 bg-dot-pattern" />
        <div className="relative section-container py-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="hero-pill mb-4 inline-flex">
                <span className="w-2 h-2 rounded-full bg-crimson-300 animate-pulse"></span>
                Live Updates
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '-0.03em' }}>
                Blood Requests
              </h1>
              <p className="text-crimson-200 text-lg mt-2">Help save lives by responding to urgent blood donation requests</p>
            </div>
            <div className="stat-glass px-8 py-5 text-center shrink-0">
              <div className="text-4xl font-black text-white">{activeCount}</div>
              <div className="text-crimson-200 text-sm mt-1">
                {selectedDistrict || selectedBloodGroup ? 'Filtered' : 'Active'} Requests
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <path d="M0 40L1440 40L1440 20C1200 30 800 40 400 30C200 25 100 20 0 20V40Z" fill="#fafafa"/>
          </svg>
        </div>
      </div>

      <div className="section-container py-10">

        {/* ── Filters ── */}
        <div className="card p-5 mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            <div>
              <label className="field-label flex items-center gap-1.5">
                <svg className="w-4 h-4 text-surface-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                District
              </label>
              <select
                value={selectedDistrict}
                onChange={e => setSelectedDistrict(e.target.value)}
                className="input-field"
              >
                <option value="">All Districts</option>
                {DISTRICTS_KERALA.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            <div>
              <label className="field-label flex items-center gap-1.5">
                <span className="text-crimson-500">🩸</span>
                Blood Group
              </label>
              <select
                value={selectedBloodGroup}
                onChange={e => setSelectedBloodGroup(e.target.value)}
                className="input-field"
              >
                <option value="">All Blood Groups</option>
                {BLOOD_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>

            <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-2">
              {/* Active filters */}
              <div className="flex-1 flex flex-wrap gap-2 items-center">
                {selectedDistrict && (
                  <span className="badge-info inline-flex items-center gap-1.5">
                    📍 {selectedDistrict}
                    <button onClick={() => setSelectedDistrict('')} className="ml-1 text-sky-800 hover:text-sky-900 font-bold">×</button>
                  </span>
                )}
                {selectedBloodGroup && (
                  <span className="badge-crimson inline-flex items-center gap-1.5">
                    🩸 {selectedBloodGroup}
                    <button onClick={() => setSelectedBloodGroup('')} className="ml-1 text-crimson-800 hover:text-crimson-900 font-bold">×</button>
                  </span>
                )}
                {!selectedDistrict && !selectedBloodGroup && (
                  <span className="text-sm text-surface-400">No filters active</span>
                )}
              </div>
              {(selectedDistrict || selectedBloodGroup) && (
                <button
                  onClick={() => { setSelectedDistrict(''); setSelectedBloodGroup(''); }}
                  className="btn btn-ghost btn-sm text-surface-500 hover:text-surface-700 shrink-0"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Donor compatibility banner */}
        {currentDonor && (
          <div className="mb-6 p-4 rounded-xl bg-sky-50 border border-sky-200 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center text-xl shrink-0">ℹ️</div>
            <div>
              <span className="text-sky-800 font-semibold text-sm">Your blood group: </span>
              <span className="text-sky-900 font-black text-sm">{currentDonor.bloodGroup}</span>
              <span className="text-sky-700 text-sm"> · Compatible requests are highlighted with a green border.</span>
            </div>
          </div>
        )}

        {/* ── Requests Grid ── */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {[1,2,3,4,5,6].map(i => <CardSkeleton key={i} />)}
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center py-24">
            <div className="text-7xl mb-6">🩸</div>
            <h3 className="text-xl font-bold text-surface-700 mb-2">No Active Requests</h3>
            <p className="text-surface-500">
              {selectedDistrict || selectedBloodGroup
                ? 'No requests match your current filters. Try removing some filters.'
                : 'There are no active blood requests at the moment. Check back soon.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filteredRequests.map(request => {
              const isCompatible = currentDonor && isCompatibleDonor(request.bloodGroup, currentDonor.bloodGroup);
              const hasExpressedInterest = currentDonor && request.interestedDonors?.includes(currentDonor.id);
              const reqLoading = actionLoading[request.id];
              const urgency = getUrgencyConfig(request.urgencyLevel);
              const status = getStatusConfig(request.status);
              const isExpired = ['expired', 'cancelled'].includes(request.status);
              const isFulfilled = request.status === 'fulfilled';

              return (
                <div
                  key={request.id}
                  className={`request-card ${
                    isCompatible && !isFulfilled && !isExpired
                      ? 'request-card-compatible'
                      : isFulfilled
                      ? 'request-card-fulfilled'
                      : isExpired
                      ? 'border-surface-200 bg-surface-50 opacity-70'
                      : request.urgencyLevel === 'critical'
                      ? `request-card-critical ${urgency.glow}`
                      : 'request-card-default'
                  }`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-bold text-surface-800 text-base leading-tight">{request.patientName}</h3>
                        {isCompatible && !isExpired && !isFulfilled && (
                          <span className="badge-success text-xs">✓ Match</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={urgency.badgeCls}>{urgency.label} Priority</span>
                        <span className={status.badgeCls}>{status.icon} {status.label}</span>
                      </div>
                    </div>
                    {/* Blood group */}
                    <div className="blood-group-badge ml-3 shrink-0">{request.bloodGroup}</div>
                  </div>

                  {/* Info grid */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-start gap-2 text-sm text-surface-600">
                      <svg className="w-4 h-4 mt-0.5 text-surface-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span>{request.hospital?.name}, <span className="text-surface-500">{request.hospital?.district}</span></span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-surface-600">
                      <svg className="w-4 h-4 text-surface-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <a href={`tel:${request.contactPerson?.phoneNumber}`} className="text-crimson-600 hover:text-crimson-700 font-medium">
                        {request.contactPerson?.name} · {request.contactPerson?.phoneNumber}
                      </a>
                    </div>
                    <div className="flex items-center justify-between text-xs text-surface-400">
                      <span>{getTimeAgo(request.createdAt)}</span>
                      <span className="font-semibold text-surface-600">
                        {request.unitsRequired} unit{request.unitsRequired > 1 ? 's' : ''} needed
                      </span>
                    </div>
                  </div>

                  {/* Donor counts */}
                  {(request.interestedDonors?.length > 0 || request.acceptedDonors?.length > 0) && (
                    <div className="flex gap-2 mb-4">
                      {request.interestedDonors?.length > 0 && (
                        <span className="badge-info text-xs">
                          👥 {request.interestedDonors.length} interested
                        </span>
                      )}
                      {request.acceptedDonors?.length > 0 && (
                        <span className="badge-success text-xs">
                          ✓ {request.acceptedDonors.length} ready
                        </span>
                      )}
                    </div>
                  )}

                  {/* Notes */}
                  {request.additionalNotes && (
                    <div className="mb-4 p-3 bg-surface-50 rounded-xl text-xs text-surface-600 leading-relaxed border border-surface-100">
                      {request.additionalNotes}
                    </div>
                  )}

                  {/* ── Actions ── */}
                  {!isExpired && !isFulfilled && (
                    <div className="pt-4 border-t border-surface-100 space-y-2">
                      <button
                        onClick={() => handleReadyToDonate(request.id)}
                        disabled={reqLoading === 'accept'}
                        className="btn btn-primary btn-sm w-full"
                      >
                        {reqLoading === 'accept' ? '⏳ Processing…' : '🩸 I\'m Ready to Donate'}
                      </button>

                      <div className="grid grid-cols-2 gap-2">
                        <a href={`tel:${request.contactPerson?.phoneNumber}`} className="btn btn-success btn-sm text-xs">
                          📞 Call Now
                        </a>

                        {currentDonor && (
                          <>
                            {!hasExpressedInterest ? (
                              <button
                                onClick={() => handleExpressInterest(request.id)}
                                disabled={reqLoading === 'interest'}
                                className="btn btn-ghost btn-sm text-xs border border-surface-200 hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
                              >
                                {reqLoading === 'interest' ? '…' : '🤝 Interested'}
                              </button>
                            ) : (
                              <span className="btn btn-ghost btn-sm text-xs border border-green-200 bg-green-50 text-green-700 cursor-default">
                                ✓ Interested
                              </span>
                            )}
                          </>
                        )}

                        {!currentDonor && (
                          <div className="col-span-1">
                            {/* placeholder for alignment */}
                          </div>
                        )}
                      </div>

                      {currentDonor && (
                        <button
                          onClick={() => handleFulfillRequest(request.id)}
                          disabled={reqLoading === 'fulfill'}
                          className="btn btn-ghost btn-sm w-full text-xs text-surface-500 hover:text-surface-700"
                        >
                          {reqLoading === 'fulfill' ? '⏳ Marking…' : '✅ Mark as Fulfilled'}
                        </button>
                      )}
                    </div>
                  )}

                  {isFulfilled && (
                    <div className="pt-4 border-t border-sky-100 text-center">
                      <span className="badge-info text-sm">✅ This request has been fulfilled</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Help Guide */}
        {!loading && filteredRequests.length > 0 && (
          <div className="mt-10 card p-6">
            <h3 className="font-bold text-surface-800 mb-4 flex items-center gap-2">
              <span className="text-xl">💡</span> How to Help
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: '🩸', title: "I'm Ready to Donate", desc: 'Confirm your willingness to donate now' },
                { icon: '🤝', title: 'Express Interest',    desc: 'Let the requester know you\'re considering' },
                { icon: '📞', title: 'Call Directly',       desc: 'Contact the person for immediate coordination' },
                { icon: '✅', title: 'Mark as Fulfilled',   desc: 'Update status after successful donation' },
              ].map(({ icon, title, desc }) => (
                <div key={title} className="flex items-start gap-3 p-3 rounded-xl bg-surface-50">
                  <span className="text-xl mt-0.5 shrink-0">{icon}</span>
                  <div>
                    <div className="text-sm font-semibold text-surface-700">{title}</div>
                    <div className="text-xs text-surface-500">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Non-Registered Donor Modal ── */}
      {showDonorModal && (
        <div className="modal-overlay" onClick={() => setShowDonorModal(false)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-surface-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-surface-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Your Donor Details</h3>
                <button
                  onClick={() => setShowDonorModal(false)}
                  className="w-8 h-8 rounded-full bg-surface-100 flex items-center justify-center text-surface-500 hover:bg-surface-200 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-surface-500 mt-1">Provide your contact so the requester can reach you</p>
            </div>

            <form onSubmit={handleDonorFormSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="field-label">Full Name *</label>
                  <input type="text" value={donorFormData.name}
                    onChange={e => setDonorFormData(p => ({ ...p, name: e.target.value }))}
                    className="input-field" placeholder="Your full name" required />
                </div>
                <div>
                  <label className="field-label">Phone *</label>
                  <input type="tel" value={donorFormData.phoneNumber}
                    onChange={e => setDonorFormData(p => ({ ...p, phoneNumber: e.target.value }))}
                    className="input-field" placeholder="10-digit number" required />
                </div>
                <div>
                  <label className="field-label">Blood Group *</label>
                  <select value={donorFormData.bloodGroup}
                    onChange={e => setDonorFormData(p => ({ ...p, bloodGroup: e.target.value }))}
                    className="input-field" required>
                    <option value="">Select</option>
                    {BLOOD_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Location *</label>
                  <input type="text" value={donorFormData.location}
                    onChange={e => setDonorFormData(p => ({ ...p, location: e.target.value }))}
                    className="input-field" placeholder="City/Town" required />
                </div>
                <div>
                  <label className="field-label">District *</label>
                  <select value={donorFormData.district}
                    onChange={e => setDonorFormData(p => ({ ...p, district: e.target.value }))}
                    className="input-field" required>
                    <option value="">Select</option>
                    {DISTRICTS_KERALA.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              <p className="text-xs text-surface-400 text-center">Your info will only be shared with the blood requester</p>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowDonorModal(false)} className="btn btn-ghost btn-md flex-1 border border-surface-200">
                  Cancel
                </button>
                <button type="submit" disabled={actionLoading[selectedRequestId] === 'accept'} className="btn btn-primary btn-md flex-1">
                  {actionLoading[selectedRequestId] === 'accept' ? '⏳ Submitting…' : '🩸 Confirm Donation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog isOpen={isOpen} onClose={handleClose} onConfirm={config.onConfirm}
        title={config.title} message={config.message} confirmText={config.confirmText}
        cancelText={config.cancelText} type={config.type} />
    </div>
  );
};

export default BloodRequestsDashboard;
